import { all, put, race, select, take, takeEvery } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import { getLocale, getToken } from '@global/selectors'

import config from '../../../../../../config'

import { LOAD_BLOCK_DATA, BLOCK_DATA_REQUEST } from './constants'
import {
    loadBlockDataRequest,
    blockDataStartLoading,
    blockDataLoaded,
    blockDataLoadFailed,
    blockDataEmpty,
} from './actions'
import { changeBlockLevel } from './components/BlockTitle/components/LevelPicker/actions'

const BLOCK_DATA_REQUEST_SUCCESS = (module, id, blockKey, namespace) =>
    `${BLOCK_DATA_REQUEST(module, id, blockKey, namespace)}_${SUCCESS}`
const BLOCK_DATA_REQUEST_FAILED = (module, id, blockKey, namespace) =>
    `${BLOCK_DATA_REQUEST(module, id, blockKey, namespace)}_${FAILED}`

function* loadBlockData(_action) {
    const module = _.get(_action, 'data.module', null)
    const id = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    const level = _.get(_action, 'data.level', null)
    if (!module || !id || !blockKey || _.isNil(level)) return null

    yield put(blockDataStartLoading(module, id, blockKey))

    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const lang = config.languages[locale].requestKey

    let blockConfig = _.get(config, `api.${module}.${blockKey}.block`, {})
    blockConfig = _.isPlainObject(blockConfig) ? [blockConfig] : blockConfig

    const data = yield all({
        response: all(
            _.map(blockConfig, (request, index) =>
                race({
                    success: take(BLOCK_DATA_REQUEST_SUCCESS(module, id, blockKey, index)),
                    failed: take(BLOCK_DATA_REQUEST_FAILED(module, id, blockKey, index)),
                })
            )
        ),
        request: all(
            _.map(blockConfig, (request, index) =>
                put(
                    loadBlockDataRequest(module, id, blockKey, index, locale, {
                        ..._.get(blockConfig, index, {}),
                        qs: {
                            // TODO - portfolio quick hack - block url is defined by query string parameter (correct -> no qs from config)
                            ..._.get(blockConfig, `${index}.qs`, {}),
                            [config.modules[module].blockIdKey]: id,
                            lang,
                            token,
                            level,
                        },
                        uriParams: {
                            [config.modules[module].blockIdKey]: id,
                            lang,
                        },
                        headers: {
                            'X-Token': token,
                        },
                    })
                )
            )
        ),
    })

    const responses = _.get(data, 'response', [])
    const allResponsesSuccess = _.every(responses, 'success')
    if (allResponsesSuccess) {
        // Data for blocks without tabs should contain a property `empty` to show that the block is empty (EXCLUDING ESTATES AND PORTFOLIO)
        // When every endpoint has the property set to true (ignore undefined or null);
        // Assume that the block is empty (disable = true)
        // Otherwise open the block and deal with either data (show content) or failed response (show alert message)

        // Filter out responses with no empty property
        const filteredResponses = _.filter(responses, (r) => !_.isNil(_.get(r, 'success.payload.response.body.empty')))
        // If there are no responses left set empty as false
        // Reason: _.every - this method returns true for empty collections because everything is true of elements of empty collections.
        const dataIsEmpty = _.isEmpty(filteredResponses)
            ? false
            : _.every(filteredResponses, (r) => _.get(r, 'success.payload.response.body.empty', false))

        if (dataIsEmpty) {
            // If block has empty data (disable true, open false) -> closed block, disabled
            yield put(blockDataEmpty(module, id, blockKey))
        } else {
            // Successfully loaded block (disable false, open true) -> show content
            const blockData = _.map(responses, (response) => _.get(response, 'success.payload.response.body', null))
            yield put(
                blockDataLoaded(
                    module,
                    id,
                    blockKey,
                    locale,
                    _.size(blockData) === 1 ? _.first(blockData) : blockData,
                    level
                )
            )
            yield put(changeBlockLevel(module, id, blockKey, level))
        }
    } else {
        // Failed request for data (disable false, open true) -> show alert message instead of content
        const errorCodes = _.map(responses, (response) => _.get(response, 'failed.payload.error.code', 500))
        yield put(blockDataLoadFailed(module, id, blockKey, errorCodes))
    }

    return null
}

export default function* workspaceBlockSaga() {
    yield takeEvery(LOAD_BLOCK_DATA, loadBlockData)
}
